import React, { useEffect, useState } from 'react';
import { useSeason } from '../../context/SeasonContext';
import {
  getReenrollmentStatus,
  createReenrollmentCampaign,
  openReenrollmentCampaign,
  reopenReenrollmentCampaign,
  closeReenrollmentCampaign,
  seedReenrollmentTargets,
  postReenrollmentPoll,
  inspectReenrollmentPoll,
  linkReenrollmentPoll,
  syncReenrollmentPoll,
  runReenrollmentEscalation,
  affirmReenrollmentTarget,
  declineReenrollmentTarget,
  updateReenrollmentReferral,
  linkReenrollmentTarget,
  startReenrollmentCatFacts,
  stopReenrollmentCatFacts,
} from '../../adminApi';
import styles from '../../styles/AdminDashboard.module.css';
import panelStyles from './ReenrollmentPanel.module.css';

const ANSWER_KEY_OPTIONS = [
  { value: 'affirm', label: 'Affirm (playing again)' },
  { value: 'decline', label: 'Decline (sitting out)' },
  { value: 'catfacts', label: 'Cat Facts easter egg' },
  { value: 'referral', label: 'Referral note' },
  { value: 'ignore', label: 'Ignore' },
];

const ReenrollmentPanel = () => {
  const { effectiveSeason } = useSeason();
  const [season, setSeason] = useState(effectiveSeason);
  const [campaign, setCampaign] = useState(null);
  const [targets, setTargets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [chaseOnly, setChaseOnly] = useState(false);
  const [pollChannelConfig, setPollChannelConfig] = useState(null);
  const [linkUserId, setLinkUserId] = useState({});
  const [busy, setBusy] = useState(false);
  const [pollUrl, setPollUrl] = useState('');
  const [pollPreview, setPollPreview] = useState(null);
  const [answerMapping, setAnswerMapping] = useState({});
  const [escalationResult, setEscalationResult] = useState(null);

  const formatEscalationMessage = (result) => {
    if (!result) return 'Escalation tick ran';
    if (result.idle) return 'Escalation idle — no open campaign';
    const synced = result.pollSync?.synced ?? 0;
    const skipped = result.pollSync?.skipped ? ` (poll sync skipped: ${result.pollSync.reason})` : '';
    const anchor = result.escalationAnchorSource === 'poll'
      ? 'clock from poll'
      : 'clock from campaign open';
    const parts = [
      `Synced ${synced} voters`,
      `DM ${result.dmSent || 0}`,
      `SMS ${result.smsSent || 0}`,
      `Email stub ${result.emailStubbed || 0}`,
    ];
    const errors = (result.errors && result.errors.length)
      ? ` · errors: ${result.errors.join('; ')}`
      : '';
    return `Escalation (${anchor}): ${parts.join(' · ')}${skipped}${errors}`;
  };

  const handleRunEscalation = async () => {
    if (!campaign) return;
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      const { data } = await runReenrollmentEscalation(campaign.id);
      setEscalationResult(data);
      setSuccess(formatEscalationMessage(data));
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Escalation failed');
    } finally {
      setBusy(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await getReenrollmentStatus(season);
      setCampaign(data.campaign);
      setPollChannelConfig(data.pollChannelConfig || null);
      let nextTargets = data.targets || [];
      if (chaseOnly && data.campaign) {
        nextTargets = nextTargets.filter(
          (t) => t.status === 'pending' && (t.dm_sent_count > 0 || t.sms_sent_count > 0)
        );
      }
      setTargets(nextTargets);
      setSummary(data.summary);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [season, chaseOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, okMessage) => {
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      await fn();
      if (okMessage) setSuccess(okMessage);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleInspectPoll = async () => {
    if (!campaign || !pollUrl.trim()) return;
    try {
      setBusy(true);
      setError(null);
      setSuccess(null);
      const { data } = await inspectReenrollmentPoll(campaign.id, pollUrl.trim());
      setPollPreview(data);
      setAnswerMapping(data.suggestedMapping || {});
      setSuccess('Poll inspected — review answer mapping below, then link it.');
    } catch (err) {
      setPollPreview(null);
      setError(err.response?.data?.message || err.message || 'Failed to inspect poll');
    } finally {
      setBusy(false);
    }
  };

  const handleLinkPoll = async () => {
    if (!campaign || !pollUrl.trim()) return;
    await run(async () => {
      await linkReenrollmentPoll(campaign.id, pollUrl.trim(), answerMapping);
      setPollPreview(null);
    }, 'Existing poll linked — you can sync votes now');
  };

  const targetNoteInput = (t) => (
    <input
      type="text"
      defaultValue={t.referral_note || ''}
      placeholder="Referral / note"
      className={panelStyles.textInput}
      onBlur={(e) => {
        const value = e.target.value;
        if (value !== (t.referral_note || '')) {
          run(() => updateReenrollmentReferral(t.id, value));
        }
      }}
    />
  );

  const targetActions = (t) => (
    <div className={panelStyles.rowActions}>
      <button
        type="button"
        disabled={busy}
        onClick={() => run(() => affirmReenrollmentTarget(t.id), `Affirmed #${t.id}`)}
      >
        Confirm
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={() => run(() => declineReenrollmentTarget(t.id), `Declined #${t.id}`)}
      >
        Decline
      </button>
      {!t.user_id && (
        <>
          <input
            type="number"
            placeholder="user id"
            value={linkUserId[t.id] || ''}
            onChange={(e) => setLinkUserId({ ...linkUserId, [t.id]: e.target.value })}
            className={panelStyles.textInput}
          />
          <button
            type="button"
            disabled={busy || !linkUserId[t.id]}
            onClick={() => run(
              () => linkReenrollmentTarget(t.id, Number(linkUserId[t.id])),
              'Linked'
            )}
          >
            Link
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className={panelStyles.panel}>
      <h2>Season Re-enrollment</h2>
      <p className={panelStyles.subtitle}>
        Track who is returning for the season. Declines mark accounts inactive immediately.
        Cat Facts is an easter egg only.
      </p>

      <div className={panelStyles.toolbar}>
        <label>
          Season{' '}
          <input
            type="number"
            value={season || ''}
            onChange={(e) => setSeason(Number(e.target.value))}
            className={panelStyles.textInput}
          />
        </label>
        <label className={panelStyles.checkbox}>
          <input
            type="checkbox"
            checked={chaseOnly}
            onChange={(e) => setChaseOnly(e.target.checked)}
          />
          Chase list only
        </label>
        <button type="button" className={styles.navButton} disabled={busy} onClick={() => load()}>
          Refresh
        </button>
      </div>

      {error && <p className={styles.errorMessage}>{error}</p>}
      {success && <p className={panelStyles.success}>{success}</p>}
      {escalationResult && !escalationResult.idle && (
        <div className={panelStyles.escalationResult}>
          <strong>Last escalation:</strong>{' '}
          synced {escalationResult.pollSync?.synced ?? 0}
          {' · '}DM {escalationResult.dmSent || 0}
          {' · '}SMS {escalationResult.smsSent || 0}
          {' · '}email stub {escalationResult.emailStubbed || 0}
          {escalationResult.escalationAnchorAt && (
            <>
              {' · '}anchor {new Date(escalationResult.escalationAnchorAt).toLocaleString()}
              {' '}({escalationResult.escalationAnchorSource})
            </>
          )}
        </div>
      )}

      <div className={panelStyles.actions}>
        {!campaign && (
          <button
            type="button"
            className={styles.navButton}
            disabled={busy}
            onClick={() => run(() => createReenrollmentCampaign(season), 'Campaign created (draft)')}
          >
            Create campaign
          </button>
        )}
        {campaign?.status === 'draft' && (
          <button
            type="button"
            className={styles.navButton}
            disabled={busy}
            onClick={() => run(() => openReenrollmentCampaign(campaign.id), 'Campaign opened + seeded')}
          >
            Open + seed actives
          </button>
        )}
        {campaign?.status === 'closed' && (
          <button
            type="button"
            className={styles.navButton}
            disabled={busy}
            onClick={() => run(() => reopenReenrollmentCampaign(campaign.id), 'Campaign reopened')}
          >
            Reopen campaign
          </button>
        )}
        {campaign && campaign.status !== 'closed' && (
          <>
            <button
              type="button"
              className={styles.navButton}
              disabled={busy}
              onClick={() => run(() => seedReenrollmentTargets(campaign.id), 'Targets seeded')}
            >
              Re-seed actives
            </button>
            <button
              type="button"
              className={styles.navButton}
              disabled={busy}
              onClick={() => run(() => closeReenrollmentCampaign(campaign.id), 'Campaign closed')}
            >
              Close campaign
            </button>
          </>
        )}
      </div>

      {campaign && (
        <div className={panelStyles.meta}>
          <div>
            <strong>Status:</strong> {campaign.status} · id {campaign.id}
          </div>
          <div>
            <strong>Delays:</strong> DM {campaign.dm_delay_hours}h · SMS {campaign.sms_delay_hours}h · Email stub{' '}
            {campaign.email_delay_hours}h
          </div>
          <div>
            <strong>Poll channel:</strong>{' '}
            {pollChannelConfig
              ? `${pollChannelConfig.display_name} (${pollChannelConfig.channel_id})`
              : 'not configured — set under Admin → Discord Channels → Season Re-enrollment'}
          </div>
          <div>
            <strong>Poll message:</strong>{' '}
            {campaign.discord_message_id
              ? `${campaign.discord_channel_id} / ${campaign.discord_message_id}`
              : 'not posted'}
          </div>
          <div>
            <strong>Escalation clock:</strong>{' '}
            {campaign.escalation_anchor_at
              ? `${new Date(campaign.escalation_anchor_at).toLocaleString()} (${
                campaign.escalation_anchor_source === 'poll' ? 'poll posted' : 'campaign opened'
              })`
              : 'not set yet'}
          </div>
          <div>
            <strong>Last escalation:</strong>{' '}
            {campaign.last_escalation_at
              ? new Date(campaign.last_escalation_at).toLocaleString()
              : 'never'}
          </div>
          <div>
            <strong>Next escalation (cron):</strong>{' '}
            {campaign.next_escalation_at
              ? new Date(campaign.next_escalation_at).toLocaleString()
              : '—'}
          </div>
          {campaign.poll_answer_mapping && (
            <div>
              <strong>Answer mapping:</strong>{' '}
              {Object.entries(campaign.poll_answer_mapping)
                .map(([id, key]) => `#${id}→${key}`)
                .join(', ')}
            </div>
          )}
          {summary && (
            <div className={panelStyles.chips}>
              <span>pending {summary.pending}</span>
              <span>affirmed {summary.affirmed}</span>
              <span>declined {summary.declined}</span>
              <span>easter_egg {summary.easter_egg}</span>
              <span>cat facts {summary.catfacts_opt_in ?? 0}</span>
              <span>unlinked {summary.unlinked}</span>
            </div>
          )}
        </div>
      )}

      {campaign?.status === 'open' && (
        <>
          <div className={panelStyles.pollRow}>
            <button
              type="button"
              className={styles.navButton}
              disabled={busy || !pollChannelConfig}
              title={
                pollChannelConfig
                  ? `Post poll to ${pollChannelConfig.display_name}`
                  : 'Configure Season Re-enrollment channel under Discord Channels first'
              }
              onClick={() => run(
                () => postReenrollmentPoll(campaign.id),
                'Poll posted'
              )}
            >
              Post Discord poll
            </button>
            <button
              type="button"
              className={styles.navButton}
              disabled={busy || !campaign.discord_message_id}
              onClick={() => run(() => syncReenrollmentPoll(campaign.id), 'Poll synced')}
            >
              Sync poll votes
            </button>
            <button
              type="button"
              className={styles.navButton}
              disabled={busy}
              onClick={handleRunEscalation}
            >
              Run escalation now
            </button>
            <button
              type="button"
              className={styles.navButton}
              disabled={busy}
              onClick={() => run(() => startReenrollmentCatFacts(campaign.id), 'Cat Facts started')}
            >
              Start Cat Facts drip
            </button>
            <button
              type="button"
              className={styles.navButton}
              disabled={busy}
              onClick={() => run(() => stopReenrollmentCatFacts(), 'Cat Facts stopped')}
            >
              Stop Cat Facts
            </button>
          </div>

          <div className={panelStyles.linkPoll}>
            <h3>Link existing Discord poll</h3>
            <p className={panelStyles.subtitle}>
              Paste a Discord message URL for a poll already in a channel. We guess answer mappings;
              adjust them, link, then sync like a normal campaign poll.
            </p>
            <div className={panelStyles.pollRow}>
              <input
                type="text"
                className={`${panelStyles.textInput} ${panelStyles.urlInput}`}
                placeholder="https://discord.com/channels/guild/channel/message"
                value={pollUrl}
                onChange={(e) => setPollUrl(e.target.value)}
              />
              <button
                type="button"
                className={styles.navButton}
                disabled={busy || !pollUrl.trim()}
                onClick={handleInspectPoll}
              >
                Inspect poll
              </button>
              <button
                type="button"
                className={styles.navButton}
                disabled={busy || !pollPreview}
                onClick={handleLinkPoll}
              >
                Link + save mapping
              </button>
            </div>

            {pollPreview && (
              <div className={panelStyles.mappingBox}>
                <div>
                  <strong>Question:</strong> {pollPreview.question || '(none)'}
                  {pollPreview.allowMultiselect ? ' · multi-select' : ' · single-select'}
                  {pollPreview.pollPostedAt
                    ? ` · posted ${new Date(pollPreview.pollPostedAt).toLocaleString()}`
                    : ''}
                </div>
                <table className={panelStyles.table}>
                  <thead>
                    <tr>
                      <th>Answer</th>
                      <th>Votes</th>
                      <th>Maps to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pollPreview.answers.map((answer) => (
                      <tr key={answer.id}>
                        <td>{answer.text}</td>
                        <td>{answer.voteCount}</td>
                        <td>
                          <select
                            value={answerMapping[answer.id] || 'ignore'}
                            onChange={(e) => setAnswerMapping({
                              ...answerMapping,
                              [answer.id]: e.target.value,
                            })}
                            className={panelStyles.textInput}
                          >
                            {ANSWER_KEY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          <div className={panelStyles.tableWrap}>
            <table className={panelStyles.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Discord</th>
                  <th>Status</th>
                  <th>Source</th>
                  <th>Channels</th>
                  <th>SMS / Email</th>
                  <th>Note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((t) => (
                  <tr key={t.id} className={t.user_id ? undefined : panelStyles.unlinked}>
                    <td>{t.username || '(unlinked)'}</td>
                    <td>{t.discord_username || t.discord_user_id || '—'}</td>
                    <td>{t.status}{t.catfacts_opt_in ? ' + cat facts' : ''}</td>
                    <td>{t.response_source || '—'}</td>
                    <td>
                      DM {t.dm_sent_count || 0} · SMS {t.sms_sent_count || 0} · Email {t.email_sent_count || 0}
                      {t.last_channel_sent ? ` · last ${t.last_channel_sent}` : ''}
                    </td>
                    <td>
                      {t.phone_verified_at ? 'SMS ok' : 'no SMS'}
                      {' / '}
                      {t.email ? 'email set' : 'no email'}
                    </td>
                    <td>{targetNoteInput(t)}</td>
                    <td>{targetActions(t)}</td>
                  </tr>
                ))}
                {targets.length === 0 && (
                  <tr>
                    <td colSpan={8}>No targets yet. Create/open a campaign to seed active users.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className={panelStyles.targetCards}>
            {targets.length === 0 && (
              <p>No targets yet. Create/open a campaign to seed active users.</p>
            )}
            {targets.map((t) => (
              <article
                key={t.id}
                className={`${panelStyles.targetCard} ${t.user_id ? '' : panelStyles.unlinked}`}
              >
                <h3>{t.username || '(unlinked)'}</h3>
                <dl>
                  <div className={panelStyles.cardRow}>
                    <dt>Discord</dt>
                    <dd>{t.discord_username || t.discord_user_id || '—'}</dd>
                  </div>
                  <div className={panelStyles.cardRow}>
                    <dt>Status</dt>
                    <dd>{t.status}{t.catfacts_opt_in ? ' + cat facts' : ''}</dd>
                  </div>
                  <div className={panelStyles.cardRow}>
                    <dt>Source</dt>
                    <dd>{t.response_source || '—'}</dd>
                  </div>
                  <div className={panelStyles.cardRow}>
                    <dt>Channels</dt>
                    <dd>
                      DM {t.dm_sent_count || 0} · SMS {t.sms_sent_count || 0} · Email {t.email_sent_count || 0}
                      {t.last_channel_sent ? ` · last ${t.last_channel_sent}` : ''}
                    </dd>
                  </div>
                  <div className={panelStyles.cardRow}>
                    <dt>SMS / Email</dt>
                    <dd>
                      {t.phone_verified_at ? 'SMS ok' : 'no SMS'}
                      {' / '}
                      {t.email ? 'email set' : 'no email'}
                    </dd>
                  </div>
                </dl>
                <label className={panelStyles.cardNote}>
                  Note
                  {targetNoteInput(t)}
                </label>
                {targetActions(t)}
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ReenrollmentPanel;
